/**
 * tokenStore.ts — custódia das credenciais no cliente.
 *
 * Access token:
 * - Vive em memória e no sessionStorage da aba, junto com o instante de expiração.
 * - O sessionStorage existe só para sobreviver a um F5 sem uma ida à rede; a sessão
 *   de verdade é o refresh token, que dura 30 dias.
 *
 * Refresh token:
 * - No browser é um cookie HttpOnly escrito pela API. O JavaScript nunca o vê, o que
 *   é justamente a proteção contra XSS — por isso não há nada a guardar aqui.
 * - No app nativo não há cookie confiável, então ele fica no Preferences (Keychain no
 *   iOS, EncryptedSharedPreferences no Android).
 *
 * Dica de sessão (`dkro.session-hint`):
 * - Como o cookie é invisível ao script, sem esse marcador o app não teria como saber
 *   se vale a pena tentar renovar a sessão ao abrir. Fica no localStorage porque
 *   precisa cruzar abas e reinícios do browser, exatamente como o cookie.
 */

import { environmentStorageKey } from "@/lib/appEnvironment"
import type { AppEnvironment } from "@/lib/appEnvironment"
import { Preferences } from "@capacitor/preferences"
import { isNativeApp } from "@/lib/platform"

const TOKEN_KEY = environmentStorageKey("dkro.token")
const USER_KEY = environmentStorageKey("dkro.user")
const SESSION_MARKER_KEY = environmentStorageKey("dkro.auth-session")
const EXPIRES_AT_KEY = environmentStorageKey("dkro.token-expires-at")
const REFRESH_KEY = environmentStorageKey("dkro.refresh")
const SESSION_HINT_KEY = environmentStorageKey("dkro.session-hint")

let accessToken: string | null = null
let expiresAtMs: number | null = null

function clearDocumentCookies(): void {
  if (typeof document === "undefined") return
  for (const entry of document.cookie.split(";")) {
    const name = entry.split("=")[0]?.trim()
    if (!name) continue
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=None; Secure`
  }
}

async function removeLegacyPersistentAuth(): Promise<void> {
  if (typeof window === "undefined") return
  window.localStorage.removeItem(TOKEN_KEY)
  window.localStorage.removeItem(USER_KEY)
  await Promise.allSettled([
    Preferences.remove({ key: TOKEN_KEY }),
    Preferences.remove({ key: USER_KEY }),
  ])
}

export const token = {
  get: () => accessToken,

  /**
   * Guarda o access token recém-emitido.
   *
   * @param expiresInSeconds validade informada pela API; sem ela o token é tratado
   * como já vencido, o que apenas força uma renovação na próxima request em vez de
   * arriscar usar um token morto.
   */
  set: (value: string, expiresInSeconds?: number) => {
    accessToken = value
    expiresAtMs = typeof expiresInSeconds === "number" && expiresInSeconds > 0
      ? Date.now() + expiresInSeconds * 1000
      : null

    if (typeof window === "undefined") return

    window.sessionStorage.setItem(TOKEN_KEY, value)
    window.sessionStorage.setItem(SESSION_MARKER_KEY, "active")
    window.localStorage.setItem(SESSION_HINT_KEY, "1")

    if (expiresAtMs === null) {
      window.sessionStorage.removeItem(EXPIRES_AT_KEY)
    } else {
      window.sessionStorage.setItem(EXPIRES_AT_KEY, String(expiresAtMs))
    }
  },

  /** Instante (epoch ms) em que o access token expira, ou null se desconhecido. */
  expiresAt: () => expiresAtMs,

  /**
   * O token vence dentro da janela informada? Também responde true quando a validade
   * é desconhecida, para que o chamador prefira renovar a mandar um token duvidoso.
   */
  isExpiringSoon: (withinSeconds = 30) => {
    if (!accessToken) return false
    if (expiresAtMs === null) return true
    return expiresAtMs - Date.now() <= withinSeconds * 1000
  },

  clear: () => {
    accessToken = null
    expiresAtMs = null
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(TOKEN_KEY)
      window.sessionStorage.removeItem(USER_KEY)
      window.sessionStorage.removeItem(SESSION_MARKER_KEY)
      window.sessionStorage.removeItem(EXPIRES_AT_KEY)
      window.localStorage.removeItem(TOKEN_KEY)
      window.localStorage.removeItem(USER_KEY)
      window.localStorage.removeItem(SESSION_HINT_KEY)
      clearDocumentCookies()
      void Promise.allSettled([
        Preferences.remove({ key: TOKEN_KEY }),
        Preferences.remove({ key: USER_KEY }),
        Preferences.remove({ key: REFRESH_KEY }),
      ])
    }
  },

  /**
   * Recarrega o access token da aba atual e descarta credenciais de versões antigas.
   *
   * Não devolver nada aqui não significa mais "sessão encerrada": quem chama tenta a
   * renovação silenciosa em seguida, e é ela que decide.
   */
  restore: async (): Promise<string | null> => {
    if (typeof window === "undefined") return null
    await removeLegacyPersistentAuth()

    const current = window.sessionStorage.getItem(TOKEN_KEY)
    accessToken = current

    const storedExpiry = window.sessionStorage.getItem(EXPIRES_AT_KEY)
    const parsedExpiry = storedExpiry ? Number(storedExpiry) : Number.NaN
    expiresAtMs = Number.isFinite(parsedExpiry) ? parsedExpiry : null

    if (!current) {
      window.sessionStorage.removeItem(USER_KEY)
      window.sessionStorage.removeItem(SESSION_MARKER_KEY)
      window.sessionStorage.removeItem(EXPIRES_AT_KEY)
    }

    return current
  },

  saveUser: (user: unknown) => {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(USER_KEY, JSON.stringify(user))
    }
  },

  restoreUser: async <T>(): Promise<T | null> => {
    if (typeof window === "undefined") return null
    const raw = window.sessionStorage.getItem(USER_KEY)
    if (!raw) return null
    try {
      return JSON.parse(raw) as T
    } catch {
      window.sessionStorage.removeItem(USER_KEY)
      return null
    }
  },

  // ─── Refresh token (apenas app nativo) ───────────────────────────────────

  /**
   * Persiste o refresh token do app nativo. No browser é um no-op: o valor chega
   * como cookie HttpOnly e gravá-lo em storage legível anularia a proteção.
   */
  saveRefreshToken: async (value: string | null | undefined): Promise<void> => {
    if (!isNativeApp()) return
    if (!value) {
      await Preferences.remove({ key: REFRESH_KEY })
      return
    }
    await Preferences.set({ key: REFRESH_KEY, value })
  },

  getRefreshToken: async (): Promise<string | null> => {
    if (!isNativeApp()) return null
    const { value } = await Preferences.get({ key: REFRESH_KEY })
    return value ?? null
  },

  // ─── Dica de sessão ──────────────────────────────────────────────────────

  /** Houve um login neste dispositivo que pode ainda ter refresh token válido. */
  hasSessionHint: (): boolean =>
    typeof window !== "undefined" && window.localStorage.getItem(SESSION_HINT_KEY) === "1",

  /** Chamado quando a API recusa a renovação: não há mais o que retomar. */
  clearSessionHint: (): void => {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(SESSION_HINT_KEY)
    }
  },
}

/** Clear the destination before reloading so switching always starts at login.
 * Source requests still in flight can only write to the source's scoped keys. */
export async function clearNativeEnvironmentSession(environment: AppEnvironment): Promise<void> {
  if (!isNativeApp()) return
  const keys = ["dkro.token", "dkro.user", "dkro.auth-session", "dkro.token-expires-at", "dkro.refresh", "dkro.session-hint"]
  await Promise.all(keys.map(async (key) => {
    const scopedKey = `${key}:${environment}`
    window.sessionStorage.removeItem(scopedKey)
    window.localStorage.removeItem(scopedKey)
    await Preferences.remove({ key: scopedKey })
  }))
}
